import React from 'react';
import { FluentIcon, FluentIconsProps } from '@fluentui/react-icons';

/** A pair of regular / filled icons, exactly like Fluent-UI ships. */
type IconPair = { regular: FluentIcon; filled: FluentIcon };

/**
 * Per-layer placement controls.
 *
 * Scaling uses `fontSize` (not CSS `transform`) because Safari's text-only
 * zoom doesn't apply transforms - see `bugfix/safari-library-icon`.
 *
 * The container is always `1em × 1em`, so `scale` **must be ≤ 1**.
 *
 * Offsets are authored in px-grid units and converted to em at runtime (÷ 16).
 */
interface LayerProps {
  /** Font-size multiplier (default `1`). Keep ≤ 1. */
  scale?: number;
  /** Horizontal offset in px-grid units. Positive = push left (CSS `right`). */
  offsetX?: number;
  /** Vertical offset in px-grid units. Positive = push up (CSS `bottom`). */
  offsetY?: number;
}

/** A single layer: regular + filled variant, plus optional placement. */
type Layer = IconPair & { props?: LayerProps };

/**
 * Stack two Fluent-UI icons into a single composite icon.
 *
 * Returns `{ regular, filled }` - drop-in replacement for any `FluentIcon`.
 * The `1em × 1em` wrapper inherits `fontSize` from outside, so the composite
 * scales identically to native Fluent-UI icons.
 */
export const createCompositeIcon = (base: Layer, overlay: Layer): IconPair => {
  const baseProps = { scale: 1, offsetX: 0, offsetY: 0, ...base.props };
  const overlayProps = { scale: 1, offsetX: 0, offsetY: 0, ...overlay.props };

  const makeLayer = (Base: FluentIcon, Overlay: FluentIcon): FluentIcon => {
    const Composite = React.forwardRef<HTMLElement, FluentIconsProps>((props, ref) => {
      // `fontSize` must NOT leak to inner icons - it would override
      // the per-layer `fontSize: ${scale}em`, breaking the layout.
      const { className, style, tabIndex, id, title, fontSize, ...iconProps } = props;

      return (
        <span
          ref={ref}
          className={className}
          style={{
            position: 'relative',
            display: 'inline-block',
            width: '1em',
            height: '1em',
            fontSize: fontSize ?? undefined,
            lineHeight: 0,
            ...style,
          }}
          tabIndex={tabIndex}
          id={id}
          title={title}
        >
          {/* base / background */}
          <span
            style={{
              position: 'absolute',
              bottom: `${baseProps.offsetY / 16}em`,
              right: `${baseProps.offsetX / 16}em`,
              fontSize: `${baseProps.scale}em`,
              lineHeight: 0,
            }}
          >
            <Base {...iconProps} />
          </span>

          {/* overlay / badge */}
          <span
            style={{
              position: 'absolute',
              bottom: `${overlayProps.offsetY / 16}em`,
              right: `${overlayProps.offsetX / 16}em`,
              fontSize: `${overlayProps.scale}em`,
              lineHeight: 0,
            }}
          >
            <Overlay {...iconProps} />
          </span>
        </span>
      );
    });

    return Composite as unknown as FluentIcon;
  };

  return {
    regular: makeLayer(base.regular, overlay.regular),
    filled: makeLayer(base.filled, overlay.filled),
  };
};
